import { Link, useParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { TopHeader } from "./inc/topHeader";
import { SideBar } from "./inc/sideBar";
import { HSTSToast } from "../../utils/hstsToast";
import { ToastContainer } from "react-toastify";
import {
  getProgressById,
  getProgressFile,
  updateProgress,
  updateProgressStatus,
  uploadProgressFile,
} from "../../services/ProgressService";
import { getCommentsByProgressId } from "../../services/commentService";

const EditProgress = () => {
  const { id } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState(null);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== "undefined" ? window.innerWidth : 1024
  );

  const navigator = useNavigate();
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [comments, setComments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (windowWidth < 768) {
      setSidebarOpen(false);
    }
  }, [windowWidth]);

  const toggleMenu = (menu) => {
    setActiveMenu(activeMenu === menu ? null : menu);
  };

  const [progressData, setProgressData] = useState({
    previous: "",
    currently: "",
    next: "",
    documentPath: "",
    meetSupervisor: "",
    supervisorResponse: "",
    meetProgressPlan: "",
    submitThesis: "",
    studyComplition: "",
    challenges: "",
    suggestion: "",
    progressStatus: "On Progress",
  });

  useEffect(() => {
    if (!id) return;

    const fetchProgress = async () => {
      try {
        const res = await getProgressById(id);
        setProgressData(res.data);
      } catch (err) {
        console.error("Error fetching progress:", err);
        setError("Failed to load progress data");
      } finally {
        setIsLoading(false);
      }
    };

    const fetchComments = async () => {
      try {
        const res = await getCommentsByProgressId(id);
        setComments(res.data || []);
      } catch (err) {
        console.error("Error fetching comments:", err);
      }
    };

    fetchProgress();
    fetchComments();
  }, [id]);

  const validateField = (name, value) => {
    let error = "";

    const requiredFieldsStep1 = [
      "previous",
      "currently",
      "next",
      "documentPath",
      "meetSupervisor",
      "supervisorResponse",
    ];

    const requiredFieldsStep2 = [
      "meetProgressPlan",
      "submitThesis",
      "studyComplition",
      "challenges",
      "suggestion",
    ];

    if (
      (step === 1 && requiredFieldsStep1.includes(name)) ||
      (step === 2 && requiredFieldsStep2.includes(name))
    ) {
      if (!value || value.trim() === "") {
        error = "This field is required.";
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProgressData((prev) => ({ ...prev, [name]: value }));
    if (touched[name]) validateField(name, value);
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setProgressData((prev) => ({ ...prev, [name]: value }));
    setTouched((prev) => ({ ...prev, [name]: true }));
    validateField(name, value);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      setProgressData((prev) => ({ ...prev, documentPath: file.name }));
      setTouched((prev) => ({ ...prev, documentPath: true }));
      validateField("documentPath", file.name);
    }
  };

  const handleNext = () => {
    const requiredFields = [
      "previous",
      "currently",
      "next",
      "documentPath",
      "meetSupervisor",
      "supervisorResponse",
    ];

    const newErrors = {};
    requiredFields.forEach((field) => {
      validateField(field, progressData[field]);
      if (!progressData[field] || progressData[field].trim() === "") {
        newErrors[field] = "This field is required.";
      }
    });

    setErrors(newErrors);
    setTouched((prev) => ({
      ...prev,
      ...Object.fromEntries(requiredFields.map((f) => [f, true])),
    }));

    if (Object.keys(newErrors).length === 0) {
      setStep(2);
    } else {
      HSTSToast("error", "Please fill all required fields.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const requiredStep2 = [
      "meetProgressPlan",
      "submitThesis",
      "studyComplition",
      "challenges",
      "suggestion",
    ];

    const newErrors = {};
    requiredStep2.forEach((field) => {
      validateField(field, progressData[field]);
      if (!progressData[field] || progressData[field].trim() === "") {
        newErrors[field] = "This field is required.";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched((prev) => ({
        ...prev,
        ...Object.fromEntries(requiredStep2.map((f) => [f, true])),
      }));
      HSTSToast("error", "Please complete all required fields.");
      return;
    }

    const confirm = window.confirm(
      "Are you sure you want to update this progress report?"
    );

    if (!confirm) return;

    try {
      await updateProgress(id, progressData);

      await updateProgressStatus(id, "On Progress");

      if (file) {
        await uploadProgressFile(id, file);
      }

      HSTSToast("success", "Progress updated successfully");
      setTimeout(() => navigator(-1), 1800);
    } catch (err) {
      console.error("Update error:", err);
      HSTSToast("error", "Update failed, please try again");
    }
  };

  const getInputClass = (name) => {
    if (!touched[name]) return "form-control";
    return errors[name] ? "form-control is-invalid" : "form-control is-valid";
  };

  const handleOpenFile = () => {
    if (progressData.documentPath) {
      const fileUrl = getProgressFile(progressData.documentPath);
      window.open(fileUrl, "_blank");
    }
  };

  const renderCommentsTable = () => {
    if (comments.length === 0) {
      return <div className="text-center p-3 text-muted">No comments yet.</div>;
    }

    return (
      <div className="table-responsive">
        <table className="table table-striped table-bordered table-hover align-middle">
          <thead className="table-light">
            <tr>
              <th scope="col">#</th>
              <th scope="col">Comment</th>
              <th scope="col" style={{ width: "15%" }}>
                Date
              </th>
            </tr>
          </thead>
          <tbody>
            {comments.map((comment, index) => (
              <tr key={comment.commentId || index}>
                <td>{index + 1}</td>
                <td style={{ whiteSpace: "pre-wrap" }}>{comment.comment}</td>
                <td>
                  {comment.commentedDate
                    ? new Date(comment.commentedDate).toLocaleDateString()
                    : "N/A"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
        <SideBar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeMenu={activeMenu}
          toggleMenu={toggleMenu}
          windowWidth={windowWidth}
        />
        <div className="main-panel">
          <TopHeader
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            windowWidth={windowWidth}
          />
          <div className="container mt-0">
            <div className="text-center p-5">Loading progress data...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
        <SideBar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          activeMenu={activeMenu}
          toggleMenu={toggleMenu}
          windowWidth={windowWidth}
        />
        <div className="main-panel">
          <TopHeader
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            windowWidth={windowWidth}
          />
          <div className="container mt-0">
            <div className="alert alert-danger mt-4">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="d-flex position-relative" style={{ minHeight: "100vh" }}>
      <SideBar
        sidebarOpen={sidebarOpen}
        setSidebarOpen={setSidebarOpen}
        activeMenu={activeMenu}
        toggleMenu={toggleMenu}
        windowWidth={windowWidth}
      />
      <div className="main-panel">
        <TopHeader
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          windowWidth={windowWidth}
        />
        <div className="container mt-0">
          <div className="page-inner">
            <div className="page-header bg-white zis-shadow rounded-2 mt-2">
              <ul className="breadcrumbs my-md-3 mb-3 ms-0">
                <li className="nav-home">
                  <Link to="/student" className="ps-3 ps-md-0">
                    <span className="fa fa-home"></span>
                  </Link>
                </li>
                <li className="separator">
                  <i className="fas fa-chevron-right text-secondary"></i>
                </li>
                <li className="nav-item text-black fw-semibold">
                  <Link to="/student/study">Study Records</Link>
                </li>
                <li className="separator">
                  <i className="fas fa-chevron-right text-secondary"></i>
                </li>
                <li className="nav-item text-black fw-semibold">
                  <button
                    className="btn btn-link p-0 text-decoration-none text-muted"
                    onClick={() => navigator(-1)}
                  >
                    Manage progress
                  </button>
                </li>
                <li className="separator">
                  <i className="fas fa-chevron-right text-secondary"></i>
                </li>
                <li className="nav-item text-black fw-semibold">
                  <Link to="#">Edit Progress</Link>
                </li>
              </ul>
            </div>

            {comments.length > 0 && (
              <div className="col-12 mt-4">
                <div className="card zis-shadow rounded-2">
                  <div className="card-header">
                    <h4 className="card-title mb-0 mt-2 fw-semibold">
                      Comment History
                    </h4>
                  </div>
                  <div className="card-body">{renderCommentsTable()}</div>
                </div>
              </div>
            )}

            <div className="row mt-5 align-items-center h-100">
              <form
                className="col-md-5 col-12 mx-auto"
                onSubmit={step === 2 ? handleSubmit : (e) => e.preventDefault()}
              >
                <div className="card zis-shadow rounded-3">
                  <div className="card-body">
                    <div className="text-center my-3">
                      <h1 className="h5 text-black fw-semibold">
                        {step === 1
                          ? "Edit Progress Report – Step 1"
                          : "Edit Progress Evaluation – Step 2"}
                      </h1>
                    </div>

                    {/* Step 1 */}
                    {step === 1 && (
                      <>
                        {[
                          { label: "Previous Progress", name: "previous" },
                          { label: "Current Progress", name: "currently" },
                          { label: "Next Month Progress", name: "next" },
                        ].map((field) => (
                          <div key={field.name} className="form-group mb-3">
                            <label>
                              {field.label}{" "}
                              <span className="text-danger fw-bold">*</span>
                            </label>
                            <textarea
                              name={field.name}
                              className={getInputClass(field.name)}
                              value={progressData[field.name]}
                              rows={3}
                              onChange={handleChange}
                              onBlur={handleBlur}
                            />
                            {errors[field.name] && (
                              <div className="invalid-feedback">
                                {errors[field.name]}
                              </div>
                            )}
                          </div>
                        ))}

                        <div className="form-group mb-3">
                          <label>
                            Current Progress Report (PDF){" "}
                            <span className="text-danger fw-bold">*</span>
                          </label>{" "}
                          <br />
                          <div className="text-secondary mb-2 fw-bold">
                            Your report must be signed by supervisor / HOD and
                            stamped
                          </div>
                          <input
                            type="file"
                            className={getInputClass("documentPath")}
                            accept="application/pdf"
                            onChange={handleFileChange}
                            onBlur={handleBlur}
                          />
                          {errors.documentPath && (
                            <div className="invalid-feedback">
                              {errors.documentPath}
                            </div>
                          )}
                          {progressData.documentPath && (
                            <small
                              className="text-muted fw-bold zis-ponter"
                              onClick={handleOpenFile}
                              title="Click to view document"
                            >
                              Current: {progressData.documentPath}
                            </small>
                          )}
                        </div>

                        <div className="form-group mb-3">
                          <div className="mb-2">
                            When did you meet or communicate with your
                            supervisor{" "}
                            <span className="text-danger fw-bold">*</span>
                          </div>
                          <input
                            type="date"
                            name="meetSupervisor"
                            className={getInputClass("meetSupervisor")}
                            value={progressData.meetSupervisor}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.meetSupervisor && (
                            <div className="invalid-feedback">
                              {errors.meetSupervisor}
                            </div>
                          )}
                        </div>

                        <div className="form-group mb-3">
                          <div className="mb-2">
                            When did you receive last response from your
                            supervisor{" "}
                            <span className="text-danger fw-bold">*</span>
                          </div>
                          <input
                            type="date"
                            name="supervisorResponse"
                            className={getInputClass("supervisorResponse")}
                            value={progressData.supervisorResponse}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.supervisorResponse && (
                            <div className="invalid-feedback">
                              {errors.supervisorResponse}
                            </div>
                          )}
                        </div>

                        <div className="d-flex justify-content-end mb-3">
                          <button
                            type="button"
                            className="btn btn-primary rounded-2"
                            onClick={handleNext}
                          >
                            Next
                          </button>
                        </div>
                      </>
                    )}

                    {/* Step 2 */}
                    {step === 2 && (
                      <>
                        <div className="form-group mb-3">
                          <label>
                            Do you think your progress is going as you planned
                            .? <span className="text-danger fw-bold">*</span>
                          </label>
                          <div>
                            <label className="me-3">
                              <input
                                type="radio"
                                name="meetProgressPlan"
                                value="YES"
                                checked={
                                  progressData.meetProgressPlan === "YES"
                                }
                                onChange={handleRadioChange}
                                onBlur={handleBlur}
                              />{" "}
                              Yes
                            </label>
                            <label>
                              <input
                                type="radio"
                                name="meetProgressPlan"
                                value="NO"
                                checked={progressData.meetProgressPlan === "NO"}
                                onChange={handleRadioChange}
                                onBlur={handleBlur}
                              />{" "}
                              No
                            </label>
                          </div>
                          {errors.meetProgressPlan && (
                            <div className="text-danger small mt-1">
                              {errors.meetProgressPlan}
                            </div>
                          )}
                        </div>

                        <div className="form-group mb-3">
                          <label>
                            When do you expect to submit Thesis / Dissertation{" "}
                            <span className="text-danger fw-bold">*</span>
                          </label>
                          <input
                            type="date"
                            name="submitThesis"
                            className={getInputClass("submitThesis")}
                            value={progressData.submitThesis}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.submitThesis && (
                            <div className="invalid-feedback">
                              {errors.submitThesis}
                            </div>
                          )}
                        </div>

                        <div className="form-group mb-3">
                          <label>
                            Select study completion date{" "}
                            <span className="text-danger fw-bold">*</span>
                          </label>
                          <input
                            type="date"
                            name="studyComplition"
                            className={getInputClass("studyComplition")}
                            value={progressData.studyComplition}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.studyComplition && (
                            <div className="invalid-feedback">
                              {errors.studyComplition}
                            </div>
                          )}
                        </div>

                        <div className="form-group mb-3">
                          <label>
                            Challenges encountered{" "}
                            <span className="text-danger fw-bold">*</span>
                          </label>
                          <textarea
                            name="challenges"
                            className={getInputClass("challenges")}
                            value={progressData.challenges}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.challenges && (
                            <div className="invalid-feedback">
                              {errors.challenges}
                            </div>
                          )}
                        </div>

                        <div className="form-group mb-3">
                          <label>
                            Suggestions{" "}
                            <span className="text-danger fw-bold">*</span>
                          </label>
                          <textarea
                            name="suggestion"
                            className={getInputClass("suggestion")}
                            value={progressData.suggestion}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                          {errors.suggestion && (
                            <div className="invalid-feedback">
                              {errors.suggestion}
                            </div>
                          )}
                        </div>

                        <div className="d-flex justify-content-between mb-4">
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setStep(1)}
                          >
                            Back
                          </button>
                          <button type="submit" className="btn btn-success">
                            Change
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

export default EditProgress;
